import { type ReactElement, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import type { Comment } from '@/lib/types';

type Props = {
  comments: Comment[];
  onAdd: (content: string) => void;
  onDelete: (commentId: string) => void;
  error?: string | null;
};

export function CommentSection({ comments, onAdd, onDelete, error }: Props): ReactElement {
  const [content, setContent] = useState('');

  function handleSubmit(): void {
    if (!content.trim()) return;
    onAdd(content);
    setContent('');
  }

  return (
    <div className="space-y-3">
      {error && (
        <div
          role="alert"
          className="rounded-[var(--r-sm)] border border-[color:var(--err)]/30 bg-[color:var(--err)]/10 px-3 py-2 text-sm text-[var(--err)]"
        >
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          placeholder="Add a note..."
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSubmit();
          }}
          className="h-10 flex-1 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] px-3 text-sm text-[var(--ink)] placeholder:text-[var(--ink-3)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20"
        />
        <Button size="sm" onClick={handleSubmit} disabled={!content.trim()}>
          Add
        </Button>
      </div>

      {comments.length === 0 ? (
        <p className="text-sm text-[var(--ink-3)]">No notes yet.</p>
      ) : (
        <ul className="space-y-2">
          {comments.map((comment) => (
            <li
              key={comment.commentId}
              className="flex items-start justify-between gap-2 rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-surface)] p-3"
            >
              <p className="text-sm text-[var(--ink)]">{comment.content}</p>
              <button
                type="button"
                onClick={() => onDelete(comment.commentId)}
                aria-label="Delete"
                className="shrink-0 rounded-lg p-1 text-[var(--ink-3)] transition-colors hover:bg-[color:var(--err)]/10 hover:text-[var(--err)]"
              >
                          <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
